/**
 * BugEntity - Random-walking enemy
 *
 * Bugs move randomly to adjacent empty tiles every 1000ms.
 * They can press buttons and collide with bombs/player.
 */

import { BaseTile, MoveContext, MoveResult, TextureDescriptor } from "./BaseTile";
import { TILE } from "../constants/gameConstants";
import { TileAndEntity, TileIndex } from "../types";

import { allDirections } from "../utils/Direction";

export class BugEntity extends BaseTile {
  // Movement timing
  timeSinceLastMove: number = 0;
  moveInterval: number = 1000; // Move every 1000ms

  constructor({ row, col, id }: { row: number; col: number; id?: string }) {
    super(id ?? `bug-${col}-${row}-${Date.now()}`, col, row);
  }

  tileType(): TileIndex {
    return TILE.BUG;
  }

  textureDescriptor(): TextureDescriptor {
    return { type: "png", path: "tiles/bug.png" };
  }

  isEntity(): boolean {
    return true; // Bug is a movable entity
  }

  move(context: MoveContext): MoveResult {
    // Accumulate time
    this.timeSinceLastMove += context.deltaMs;

    // Not ready to move yet?
    if (this.timeSinceLastMove < this.moveInterval) {
      return { type: "wait" };
    }

    // Reset timer
    this.timeSinceLastMove = 0;

    // Get all valid adjacent directions
    const validDirections = allDirections.filter((dir) => {
      const target = context.getAtDirection(this.coordinates(), dir);
      return this.canMoveTo(target);
    });

    // No valid moves - stuck
    if (validDirections.length === 0) {
      return { type: "wall" };
    }

    // Pick random valid direction
    const direction =
      validDirections[Math.floor(Math.random() * validDirections.length)];

    // Check for bomb collision in chosen direction (both explode)
    const target = context.getAtDirection(this.coordinates(), direction);
    if (target.tile === TILE.BOMB) {
      return { type: "die", collisionWith: "tile", who: "both", direction };
    } else if (target.entity === TILE.PLAYER_START) {
      return { type: "die", collisionWith: "entity", who: "both", direction };
    } else if (target.tile === TILE.BUTTON) {
      return { type: "pressButton", direction };
    }


    return { type: "move", direction };
  }

  /**
   * Check if bug can move to target position
   */
  private canMoveTo(target: TileAndEntity): boolean {
    // Out of bounds
    if (this.isOutOfBounds(target)) {
      return false;
    }

    const { tile, entity } = target;

    if (tile === null) {
      return false;
    }

    const actAsWalls: TileIndex[] = [
      TILE.WALL,
      TILE.DOOR_GREEN,
      TILE.DOOR_ORANGE,
      TILE.DOOR_RED,
      TILE.DOOR_BLUE,
      TILE.BUTTON_DOOR,
      TILE.EXIT,
      TILE.SAND,
      TILE.WATER,
      TILE.ARROW_UP,
      TILE.ARROW_DOWN,
      TILE.ARROW_LEFT,
      TILE.ARROW_RIGHT,
      TILE.STAR,
      TILE.SUCTION_BOOTS,
      TILE.FLIPPERS,
      TILE.KEY_BLUE,
      TILE.KEY_RED,
      TILE.KEY_GREEN,
      TILE.KEY_ORANGE,
    ];

    if (actAsWalls.includes(tile)) {
      return false;
    }

    if (entity === TILE.PLAYER_START) {
      return true;
    }


    // Check entity layer - any entity blocks
    if (this.hasEntity(target)) {
      return false;
    }

    // Otherwise passable
    return true;
  }

  clone(): BugEntity {
    const copy = new BugEntity({
      col: this.col,
      row: this.row,
      id: this.id,
    });
    copy.timeSinceLastMove = this.timeSinceLastMove;
    copy.sprite = this.sprite; // Share sprite reference
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "bug",
      id: this.id,
      col: this.col,
      row: this.row,
      timeSinceLastMove: this.timeSinceLastMove,
    };
  }
}
